import { Printer } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import { capitalize, formatPackingType } from "~/utils";
import { TInventory } from "~/types/inventory";
import clsx from "clsx";

import "./print-label.css";

interface PrintLabelsProps {
  selectedInventories: TInventory[];
  all?: boolean;
  copiesPerLabel?: number;
  handlePrintRef?: (handlePrint: () => void) => void;
}

export const PrintLabels: React.FC<PrintLabelsProps> = ({
  selectedInventories,
  all,
  copiesPerLabel = 1,
  handlePrintRef,
}) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Label_AHC_Multi_${new Date().toISOString()}`,
    onBeforePrint: () => Promise.resolve(setIsPrinting(true)),
    onAfterPrint: () => Promise.resolve(setIsPrinting(false)),
    bodyClass: "ahc-label-printable-content",
  });

  useEffect(() => {
    if (handlePrintRef) {
      handlePrintRef(handlePrint);
    }
  }, [handlePrint, handlePrintRef]);

  const inventoriesWithCopies = selectedInventories.flatMap((inventory) =>
    Array(copiesPerLabel).fill(inventory)
  );

  const inventoryPairs = [];
  for (let i = 0; i < inventoriesWithCopies.length; i += 2) {
    inventoryPairs.push(inventoriesWithCopies.slice(i, i + 2));
  }

  return (
    <>
      <button
        className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-1 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
        disabled={selectedInventories.length === 0 || isPrinting}
        onClick={() => {
          if (componentRef.current) {
            handlePrint();
          } else {
            console.error("componentRef is null or content is not available.");
          }
        }}
      >
        {isPrinting ? (
          <svg
            className="animate-spin h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
        ) : (
          <Printer size={16} />
        )}
        <span>
          {isPrinting
            ? "Printing..."
            : all
              ? "Print all Labels"
              : "Print Labels"}
        </span>
      </button>

      <div className="ahc-label-printable-content">
        <div
          ref={componentRef}
          className={clsx("ahc-label-container", "w-full")}
        >
          {inventoryPairs.length > 0 ? (
            inventoryPairs.map((pair, index) => (
              <div key={index} className="ahc-label-page">
                <div className="ahc-label-pair">
                  {pair.map((inventory) => (
                    <div key={inventory.id} className="ahc-label-frame">
                      <div className="ahc-label-grid">
                        <div className="ahc-label-column ahc-label-left-column">
                          <div className="ahc-label-header">
                            <span className="ahc-label-title ahc-label-client-name-left">
                              {inventory?.client?.companyName ||
                                "Unnamed Client"}
                            </span>
                            <br />
                            <span className="inventory-ref">
                              {inventory?.inventoryRef}
                            </span>
                          </div>
                          <div className="ahc-label-details">
                            <div>
                              <strong>Client:</strong>{" "}
                              {inventory?.client?.companyName ||
                                "Unnamed Client"}
                            </div>
                            <div>
                              <strong>Ref:</strong>{" "}
                              {inventory.clientInternalRef || ""}
                            </div>
                            <div>
                              <strong>Artist:</strong>{" "}
                              {inventory.artistName || "Unknown"}
                            </div>
                            <div>
                              <strong>Type:</strong> {inventory?.itemType}
                            </div>
                            <div>
                              <strong>Dims:</strong> {inventory?.length} x{" "}
                              {inventory.width} x {inventory?.height}
                            </div>
                            <div>
                              <strong>Wt:</strong> {inventory?.weight} lb
                            </div>
                          </div>
                          <div className="ahc-label-footer-left">
                            Art Handler Collective
                            <br />
                            646 741 4047
                          </div>
                        </div>

                        <div className="ahc-label-divider"></div>

                        <div className="ahc-label-column ahc-label-right-column">
                          <div className="ahc-label-header-new">
                            <div className="ahc-label-header-left">
                              <div className="ahc-label-client-name">
                                {inventory?.client?.companyName ||
                                  "Unnamed Client"}
                              </div>
                              <div className="ahc-label-client-ref">
                                {inventory.clientInternalRef || ""}
                              </div>
                            </div>
                            <div className="ahc-label-header-right">
                              <div className="ahc-label-inventory-ref">
                                {inventory?.inventoryRef
                                  ? inventory.inventoryRef.replace(
                                      /^(PCE|PKG)(\d+)$/,
                                      "$1 $2"
                                    )
                                  : ""}
                              </div>
                            </div>
                          </div>
                          <div className="ahc-label-details">
                            <div>
                              <strong>Client:</strong>{" "}
                              {inventory?.client?.companyName ||
                                "Unnamed Client"}
                            </div>
                            <div>
                              <strong>Internal Ref:</strong>{" "}
                              {inventory.clientInternalRef || ""}
                            </div>
                            <div>
                              <strong>Artist:</strong>{" "}
                              {inventory.artistName || "Unknown"}
                            </div>
                            <div>
                              <strong>Type:</strong> {inventory?.itemType}
                            </div>
                            <div>
                              <strong>Title/Description:</strong>{" "}
                              <span className="ahc-label-title-description">
                                {inventory.title || "Untitled"}{" "}
                                {inventory.description || ""}
                              </span>
                            </div>
                            <div>
                              <strong>Packing:</strong>{" "}
                              {formatPackingType(inventory)}
                            </div>
                            <div>
                              <strong>Dims (in):</strong> {inventory?.length} x{" "}
                              {inventory.width} x {inventory?.height}
                            </div>
                            <div>
                              <strong>Weight:</strong> {inventory?.weight} lb
                            </div>
                          </div>
                          <div className="ahc-label-footer-right">
                            Art Handler Collective | 15 Cumberland Street–
                            Brooklyn NY, 11205 | www.arthandler.co
                            <br />
                            646 741 4047 | info@arthandlercollective.org
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div>No inventory selected for printing.</div>
          )}
        </div>
      </div>
    </>
  );
};
